import { GetServerSideProps } from "next";
import Head from "next/head";
import config from "../../../config";
import { EcommerceEventList } from "../../../models/Event";
import {
    Alert,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import {
    getErrorMessage,
    setupStyleMapFromStyleMap,
} from "../../../utils/helpers";
import React from "react";
import PageWrapper from "../../../components/PageWrapper";
import { COOKIE_BOXOFFICE_KEY } from "../../../stores/AuthStore";
import { tokenIsValid } from "../../../utils/jwtHelpers";
import { useRouter } from "next/router";
import { toast } from "react-hot-toast";
import ApplicationStore from "../../../stores/ApplicationStore";
import { useCartStore } from "../../../stores/CartStore";

const PRESTO_REGEX = /^3124[0-9]*/;

export default function Home(props: {
    style: EcommerceEventList;
    cartUrl: string;
}) {
    const [code, setCode] = React.useState("");

    const cartStore = useCartStore(props.cartUrl);

    const router = useRouter();

    function onChangeCode(e: React.ChangeEvent<HTMLInputElement>) {
        setCode(e.target.value.trim());
    }

    function onSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        if (!PRESTO_REGEX.test(code)) {
            toast.error("Invalid PRESTO Card Number");
            return;
        }

        if (props.cartUrl === "cne") {
            try {
                // * Check if SDA code is valid, then redirect to SDA page
                ApplicationStore.setMetadataField("prestoCard", code);
                router.push(`/store/special/202677RHHWFV`);
            } catch (err) {
                toast.error(getErrorMessage(err));
            }
        } else if (props.cartUrl === "markhamfair") {
            try {
                // * Check if SDA code is valid, then redirect to SDA page
                ApplicationStore.setMetadataField("prestoCard", code);
                router.push(`/store/special/2026D6YTVWP4`);
            } catch (err) {
                toast.error(getErrorMessage(err));
            }
        } else {
            toast.error("Invalid PRESTO Card Number");
            return;
        }
    }

    const line1 = ((): string => {
        if (props.cartUrl === "cne") {
            return "Enter your PRESTO Card number below to receive a discount on Admission & Ride tickets.";
        }
        if (props.cartUrl === "markhamfair") {
            return "Enter your PRESTO Card number below to receive a discount on Admission tickets.";
        }
        return "Enter your PRESTO Card number below to receive a discount on tickets.";
    })();

    const line2 = ((): string => {
        if (props.cartUrl === "cne") {
            return "After successfully entering your PRESTO Card number, you will be redirected to the discounted tickets page.";
        }
        if (props.cartUrl === "markhamfair") {
            return "After successfully entering your PRESTO Card number, you will be redirected to the discounted tickets page.";
        }
        return "After successfully entering your PRESTO Card number, you will be redirected to the tickets page. PLEASE NOTE that your discount will be applied at CHECKOUT and is NOT available on VIP or Premium Tickets.";
    })();

    React.useEffect(() => {
        if (props.style.newStyleMap) {
            setupStyleMapFromStyleMap(props.style.newStyleMap);
        }
    }, []);

    const pageTitle = `PRESTO Perks | AuthentiGATE`;

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="robots" content="noindex" />
            </Head>
            <PageWrapper
                eventList={props.style}
                overrideLogo={
                    "https://cdn.authentigate.ca/clients/87/presto_perks_logo.png"
                }
                noCart
            >
                <Container maxWidth="md">
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <form onSubmit={onSubmit}>
                                <Card>
                                    <CardContent>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Typography
                                                    variant="h4"
                                                    color="primaryText.main"
                                                    textAlign="center"
                                                >
                                                    PRESTO Perks Discount
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography
                                                    variant="h6"
                                                    color="primaryText.main"
                                                    textAlign="center"
                                                >
                                                    {line1}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography
                                                    variant="h6"
                                                    color="primaryText.main"
                                                    textAlign="center"
                                                >
                                                    {line2}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    variant="standard"
                                                    label="PRESTO Card Number"
                                                    value={code}
                                                    onChange={onChangeCode}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Button
                                                    type="submit"
                                                    fullWidth
                                                    disabled={!code}
                                                    variant="contained"
                                                    size="small"
                                                >
                                                    OK
                                                </Button>
                                            </Grid>
                                            {/* <Grid item xs={12}>
                                                <Typography
                                                    variant="h6"
                                                    color="primaryText.main"
                                                    textAlign="center"
                                                >
                                                    The PRESTO Perks discount is
                                                    currently unavailable.
                                                    Please check back later.
                                                </Typography>
                                            </Grid> */}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </form>
                        </Grid>
                    </Grid>
                </Container>
            </PageWrapper>
        </>
    );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    const cartUrl = context.params?.cartUrl;
    let style: any,
        error = false;

    if (
        !["cne", "pink-floyd-exhibition", "markhamfair"].includes(
            cartUrl?.toString().toLowerCase() ?? "",
        )
    ) {
        return {
            notFound: true,
        };
    }

    const boxoffice = context.req.cookies[COOKIE_BOXOFFICE_KEY];
    let salesContext = "ECOMMERCE";

    if (
        boxoffice == "true" &&
        tokenIsValid(
            context.req.cookies[
                config.COOKIE_PREFIX + "__authentigate_token_sanitized"
            ],
        )
    ) {
        salesContext = "BOXOFFICE";
    }

    const start = Date.now();

    try {
        const res = await fetch(
            `${config.BACKEND_URI}/sales/cart-style/${cartUrl}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    "X-Sales-Context": "ECOMMERCE",
                },
            },
        );
        if (res.status !== 200) {
            error = true;
            console.log(await res.text());
        } else {
            style = await res.json();
        }
    } catch (err) {
        console.log(err);
        error = true;
    }

    const end = Date.now();
    console.log(`Time to fetch data: ${end - start}ms`);

    if (style.newStyleMap) {
        setupStyleMapFromStyleMap(style.newStyleMap);
    }

    return {
        props: { style, cartUrl: cartUrl?.toString().toLowerCase() },
        notFound: error,
    };
};
